import {
  CreateButton,
  DateField,
  DeleteButton,
  List,
  NumberField,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter, useList, useModal } from "@refinedev/core";
import dayjs from "dayjs";
import { IInvoiceDataReport } from "interfaces";
import React from "react";

export const InvoiceDataList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<IInvoiceDataReport>();
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);
  const [invoiceDate, setInvoiceDate] = React.useState(null);

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IInvoiceDataReport>({
      resource: "ukho-invoice",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { imoNumber, vesselName, productName, invoiceNo, invoiceDate } =
          params;

        // filters.push({
        //   field: "imoNumber",
        //   operator: "eq",
        //   value: imoNumber ? imoNumber : undefined,
        // });

        // filters.push({
        //   field: "vesselName",
        //   operator: "contains",
        //   value: vesselName ? vesselName : undefined,
        // });

        // filters.push({
        //   field: "productName",
        //   operator: "contains",
        //   value: productName ? productName : undefined,
        // });

        // filters.push({
        //   field: "invoiceNo",
        //   operator: "contains",
        //   value: invoiceNo ? invoiceNo : undefined,
        // });

        filters.push({
          field: "invoiceDate",
          operator: "gte",
          value: invoiceDate
            ? invoiceDate[0].startOf("day").toISOString()
            : undefined,
        });

        filters.push({
          field: "invoiceDate",
          operator: "lte",
          value: invoiceDate
            ? invoiceDate[1].startOf("day").toISOString()
            : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const columns: ColumnsType<IInvoiceDataReport> = [
    {
      title: "UKHO Invoice No.",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("invoiceNo", sorters),
      sorter: true,
    },
    {
      title: "UKHO Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 150,
      className: "wrap-text",
      render: (value) =>
        value ? <DateField format="DD-MMM-YYYY" value={value} /> : "",
      defaultSortOrder: getDefaultSortOrder("IssueDate", sorters),
      sorter: true,
    },
    {
      title: "Order No.",
      dataIndex: "orderNo",
      key: "orderNo",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderNo", sorters),
      sorter: true,
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("vesselName", sorters),
      sorter: true,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Currency",
      dataIndex: "invoiceCurrency",
      key: "invoiceCurrency",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("invoiceCurrency", sorters),
      sorter: true,
    },
    {
      title: "Invoice Net Total",
      dataIndex: "invoiceNetTotal",
      key: "invoiceNetTotal",
      width: 150,
      className: "wrap-text",
      render: (value) => (
        <NumberField
          value={value}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
    },
    {
      title: "Invoice Tax Amount",
      dataIndex: "invoiceTaxAmount",
      key: "invoiceTaxAmount",
      width: 150,
      className: "wrap-text",
      render: (value) => (
        <NumberField
          value={value}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
    },
    {
      title: "Invoice Payable Amount",
      dataIndex: "invoicePayableAmount",
      key: "invoicePayableAmount",
      width: 170,
      className: "wrap-text",
      render: (value) => (
        <NumberField
          value={value}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Icons.DownloadOutlined />}
            href={record?.storageUrl}
            download={true}
          />
          <Button
            size="small"
            icon={<Icons.FilePdfOutlined />}
            download={false}
            onClick={() => {
              setRecord(record);
              show();
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
          />
        </Space>
      ),
    },
  ];

  function handleDateRangeSelection(value: any) {
    setDateSelected(true);
    setInvoiceDate(value);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields([
      "imoNumber",
      "vesselName",
      "productName",
      "invoiceNo",
    ]);

    //special case as "orderDate is not reset with form.resetFields() above"
    // searchFormProps?.form?.setFieldsValue({ invoiceDate: undefined });
  }

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Report",
          subTitle: "invoice data",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <CreateButton />
                {dateSelected ? (
                  <Button
                    icon={<Icons.ExportOutlined />}
                    // onClick={exportData}
                    // disabled={!dateSelected}
                    // loading={exportDataLoading}
                  >
                    Export
                  </Button>
                ) : (
                  <></>
                )}
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} lg={24} xs={24}>
            <Card type="inner" title={"Filter"}>
              <Filter
                formProps={searchFormProps}
                filters={filters || []}
                dateSelected={dateSelected}
                onDateRangeSelect={handleDateRangeSelection}
              />
            </Card>
          </Col>
          <Col xl={24} xs={24}>
            {dateSelected ? (
              <Table
                {...tableProps}
                columns={columns}
                rowKey="id"
                size="middle"
                scroll={{ x: 1200 }}
                sticky
                pagination={{
                  ...tableProps.pagination,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
              />
            ) : (
              <Space
                direction="vertical"
                size="small"
                style={{
                  display: "flex",
                }}
              >
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
                <Alert
                  message="Select Invoice Date"
                  description="Please select invoice date range to view the data"
                  type="info"
                  showIcon
                  style={{
                    margin: "auto",
                    width: "40%",
                    padding: "15px",
                    textAlign: "center",
                    // alignItems: "center",
                  }}
                />
              </Space>
            )}
          </Col>
        </Row>
      </List>
      <Modal
        centered
        closeIcon={<Icons.CloseSquareFilled />}
        visible={visible}
        onCancel={close}
        width="80%"
        footer={null}
      >
        <div>
          <embed
            src={record?.embedUrl}
            type="application/pdf"
            width="99%"
            height="700px"
          />
        </div>
      </Modal>
    </>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  dateSelected: boolean;
  onDateRangeSelect: any;
}> = (props) => {
  const { formProps, filters, dateSelected, onDateRangeSelect } = props;

  const { result: data, query: {
    isLoading
  } } = useList<IInvoiceDataReport>({
    resource: "ukho-invoice",

    pagination: {
      mode: "off"
    }
  });
  const flags = data?.data ?? [];

  const { selectProps: flagSelectProps } = useSelect<IInvoiceDataReport>({
    resource: "ukho-invoice",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    sorters: [
      {
        field: "flag",
        order: "asc",
      },
    ],

    pagination: {
      mode: "server",
      pageSize: flags.length || 10
    }
  });

  const { RangePicker } = DatePicker;

  const invoiceDate = React.useMemo(() => {
    const start = getDefaultFilter("invoiceDate", filters, "gte");
    const end = getDefaultFilter("invoiceDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        imoNumber: getDefaultFilter("imoNumber", filters),
        vesselName: getDefaultFilter("vesselName", filters),
        productName: getDefaultFilter("productName", filters),
        invoiceNo: getDefaultFilter("invoiceNo", filters),
        invoiceDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item
            label={"UKHO Invoice Date"}
            name="invoiceDate"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              onChange={onDateRangeSelect}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"IMO Number"} name="imoNumber">
            <Input
              placeholder="filter by IMO"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Vessel Name"} name="vesselName">
            <Input
              placeholder="filter by vessel"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Product"} name="productName">
            <Input
              placeholder="filter by product"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"UKHO Invoice No."} name="invoiceNo">
            <Input
              placeholder="filter by invoice number"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        {/* <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Issue Date"} name="issueDate">
            <RangePicker
              // defaultValue={undefined}
              value={null}
              style={{ width: "100%" }}
              disabled={!dateSelected}
              allowClear
            />
          </Form.Item>
        </Col> */}
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            style={{
              margin: "auto",
              width: "25%",
            }}
          >
            <SaveButton
              disabled={!dateSelected}
              onClick={formProps.form?.submit}
              size="large"
              block
            >
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
